import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout, { EmptyState, Pager, Spinner, StatTile } from '../components/Layout';
import Modal from '../components/Modal';
import { CardBadges, EditTypeChip, ReceptionStamp } from '../components/StatusBadge';
import CaseNotes from '../components/CaseNotes';
import AudioNote from '../components/AudioNote';
import VoiceRecorder from '../components/VoiceRecorder';
import { PrescriptionStrip } from '../components/Prescription';
import StudyPanel from '../components/StudyPanel';
import ReportComposer from './ReportComposer';
import FeeReport from '../components/FeeReport';
import { useCaseAction, useCases } from '../hooks/useCases';
import { cases as casesApi, stats as statsApi } from '../utils/api';
import { useSocket } from '../context/SocketContext';
import { clearDraft, loadDraft, saveDraft } from '../utils/screenCheckDraft';
import {
  elapsedSince,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatToman,
} from '../utils/format';

const TABS = [
  { id: 'queue', label: 'صف گزارش‌نویسی', status: 'ready_for_reporting' },
  { id: 'revision', label: 'نیازمند اصلاح', status: 'revision_requested' },
  { id: 'mine', label: 'در دست من', status: 'in_progress' },
  { id: 'review', label: 'در انتظار تأیید فنی', status: 'technical_review' },
  { id: 'done', label: 'تأییدشده‌های من', status: 'report_completed,closed' },
];

export default function EditorDashboard() {
  const [tab, setTab] = useState('queue');
  const [completing, setCompleting] = useState(null);
  const [screenChecking, setScreenChecking] = useState(null);
  const [showFees, setShowFees] = useState(false);
  // پرونده‌ای که گزارشش در حال نوشتن است. برنامه روتر ندارد، پس صفحه‌ی
  // گزارش جای کل میز را می‌گیرد و با «بازگشت» برمی‌گردیم.
  const [composing, setComposing] = useState(null);
  const { caseVersion } = useSocket();
  const [summary, setSummary] = useState(null);

  const activeTab = TABS.find((t) => t.id === tab);
  const params = useMemo(
    () => ({ status: activeTab.status, ...(tab === 'queue' ? {} : { mine: 'true' }) }),
    [activeTab.status, tab]
  );

  const { cases, count, page, setPage, pageCount, pageSize, loading, error, reload } =
    useCases(params);
  const { run, pendingId } = useCaseAction(reload);

  // فهرست پس‌زمینه تازه می‌شود؛ کارتِ باز باید همان نسخه‌ی به‌روز را بگیرد
  // وگرنه پس از بازگشت، وضعیت قدیمی نشان می‌دهد.
  const composingCase = composing
    ? cases.find((c) => c.id === composing.id) || composing
    : null;

  useEffect(() => {
    statsApi
      .get()
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [caseVersion]);


  if (composingCase) {
    return (
      <ReportComposer
        item={composingCase}
        onClose={() => {
          setComposing(null);
          reload();
        }}
      />
    );
  }

  return (
    <Layout
      title="میز ادیتور"
      subtitle="دریافت پرونده از صف، تهیه گزارش و ثبت تکمیل"
      tabs={TABS.map((t) => ({ ...t, count: t.id === tab ? count : undefined }))}
      activeTab={tab}
      onTabChange={setTab}
    >
      {summary && (
        <div className="tiles">
          <StatTile label="صف انتظار" value={formatNumber(summary.queue_length)} tone="info" />
          <StatTile
            label="نیازمند اصلاح"
            value={formatNumber(summary.my_needs_revision)}
            tone={summary.my_needs_revision > 0 ? 'danger' : 'default'}
          />
          <StatTile label="در دست من" value={formatNumber(summary.my_in_progress)} tone="brand" />
          <StatTile
            label="در انتظار تأیید فنی"
            value={formatNumber(summary.my_in_review)}
            hint="کارانه پس از تأیید ثبت می‌شود"
          />
          <StatTile
            label={`کارانه‌ی ${summary.fee_period?.label ?? 'ماه جاری'}`}
            value={formatToman(summary.my_total_fee)}
            hint={`${formatNumber(summary.my_completed)} گزارش تأییدشده`}
            tone="success"
          />
        </div>
      )}

      {summary && (
        <p className="list-footnote">
          <button type="button" className="link" onClick={() => setShowFees(true)}>
            🧾 کارنامه‌ی من — ماه جاری و ماه‌های قبل
          </button>
        </p>
      )}

      {showFees && (
        <Modal open title="کارنامه‌ی کارانه" onClose={() => setShowFees(false)} width={960}>
          <FeeReport />
        </Modal>
      )}

      {loading && <Spinner />}
      {error && <p className="alert alert--danger">{error}</p>}

      {!loading && !error && cases.length === 0 && (
        <EmptyState
          icon="🖥️"
          title={
            tab === 'queue'
              ? 'صف خالی است.'
              : tab === 'mine'
              ? 'پرونده‌ای در دست شما نیست.'
              : 'هنوز گزارشی تکمیل نکرده‌اید.'
          }
          hint={tab === 'queue' ? 'به‌محض تأیید اسکن، پرونده اینجا ظاهر می‌شود.' : undefined}
        />
      )}

      <div className="cards">
        {cases.map((item) => (
          <CaseCard
            key={item.id}
            item={item}
            pending={pendingId === item.id}
            onNoteAdded={reload}
            onClaim={() => run(item.id, () => casesApi.claim(item.id), 'پرونده به شما اختصاص یافت.')}
            onRelease={() =>
              run(item.id, () => casesApi.release(item.id), 'پرونده به صف بازگشت.')
            }
            onComplete={() => setCompleting(item)}
            onCompose={() => setComposing(item)}
            onScreenCheck={() => setScreenChecking(item)}
          />
        ))}
      </div>

      {!loading && cases.length > 0 && pageCount === 1 && (
        <p className="list-footnote">{formatNumber(count)} پرونده در این فهرست</p>
      )}

      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        count={count}
        onChange={setPage}
      />

      <CompleteModal
        item={completing}
        pending={pendingId === completing?.id}
        onClose={() => setCompleting(null)}
        onSubmit={async (payload) => {
          const result = await run(
            completing.id,
            () => casesApi.complete(completing.id, payload),
            'گزارش برای تأیید مسئول فنی ارسال شد.'
          );
          if (result) setCompleting(null);
        }}
      />

      <ScreenCheckModal
        item={screenChecking}
        pending={pendingId === screenChecking?.id}
        onClose={() => setScreenChecking(null)}
        onSubmit={async (payload) => {
          const result = await run(
            screenChecking.id,
            () => casesApi.sendScreenCheck(screenChecking.id, payload),
            'برای کارشناس فنی ارسال شد.'
          );
          if (result) {
            await clearDraft(screenChecking.id);
            setScreenChecking(null);
          }
        }}
      />
    </Layout>
  );
}

function CaseCard({ item, pending, onClaim, onRelease, onComplete, onCompose, onScreenCheck, onNoteAdded }) {
  const inProgress = item.status === 'in_progress';
  const needsRevision = item.status === 'revision_requested';
  const withEditor = inProgress || needsRevision;

  return (
    <article
      className={`card ${inProgress ? 'card--active' : ''} ${needsRevision ? 'card--revision' : ''} ${item.is_urgent ? 'card--urgent' : ''}`}
    >
      <header className="card__head">
        <div>
          <h3 className="card__title"><bdi>{item.name}</bdi></h3>
          <p className="card__meta">
            <bdi>{item.doctor}</bdi> · نسخه <span className="num">{item.order_number}</span>
          </p>
        </div>
        <CardBadges item={item} />
      </header>

      <div className="card__row">
        <EditTypeChip code={item.edit_type_code} name={item.edit_type_name} />
        {withEditor && item.start_time && <LiveTimer since={item.start_time} />}
      </div>

      {/* ادیتور باید بداند کدام پرونده زودتر پذیرش شده، نه فقط کدام تازه‌تر است */}
      <div className="card__row">
        <ReceptionStamp at={item.reception_time} />
      </div>

      {needsRevision && item.open_note && (
        <div className="revision-note">
          <p className="revision-note__head">
            📝 یادداشت مسئول فنی — نوبت {item.revision_count}
          </p>
          {item.open_note.text && <p className="revision-note__text">{item.open_note.text}</p>}
          {item.open_note.has_audio && (
            <AudioNote url={item.open_note.audio_url} seconds={item.open_note.audio_seconds} />
          )}
        </div>
      )}

      {withEditor && item.provisional_fee != null && (
        <p className="provisional">
          کارانه‌ی تخمینی تا این لحظه: <strong>{formatToman(item.provisional_fee)}</strong>
          <span className="provisional__hint">
            پس از تأیید مسئول فنی نهایی می‌شود؛ هر بار برگشت، آن را کم می‌کند.
          </span>
        </p>
      )}

      <PrescriptionStrip items={item.prescriptions} />

      <CaseNotes caseId={item.id} notes={item.notes} onChanged={onNoteAdded} />

      {item.scan_cloud_url && (
        <a className="cloud-link" href={item.scan_cloud_url} target="_blank" rel="noreferrer">
          ☁️ باز کردن فایل اسکن در فضای ابری
        </a>
      )}

      {/* تصاویر روی سرور خودمان. پس از تحویل به بیمار پاک می‌شوند، پس تا
          وقتی پرونده در دست شماست کارتان را با آن تمام کنید. */}
      <StudyPanel
        caseId={item.id}
        kind="raw"
        title="تصاویر دستگاه"
        hint="۴۸ ساعت پس از تماس با بیمار، این فایل‌ها قطعی پاک می‌شوند."
      />

      {withEditor && (
        <StudyPanel
          caseId={item.id}
          kind="edited"
          canUpload
          title="فایل ادیت‌شده"
          hint="خروجی کار خودتان را اینجا بگذارید — فولدر یا زیپ، هر کدام."
        />
      )}

      {['report_completed', 'closed'].includes(item.status) ? (
        <dl className="card__facts">
          <div>
            <dt>زمان پروژه</dt>
            <dd>{formatDuration(item.billable_seconds)}</dd>
          </div>
          <div>
            <dt>کارانه</dt>
            <dd className="fact--strong">{formatToman(item.fee)}</dd>
          </div>
          <div>
            <dt>پایان</dt>
            <dd>{formatDateTime(item.end_time)}</dd>
          </div>
        </dl>
      ) : null}

      <footer className="card__foot">
        {item.status === 'ready_for_reporting' && (
          <button type="button" className="btn btn--primary" disabled={pending} onClick={onClaim}>
            🔒 دریافت پرونده
          </button>
        )}
        {inProgress && (
          <>
            <button type="button" className="btn btn--ghost" disabled={pending} onClick={onRelease}>
              ↩️ بازگرداندن به صف
            </button>
            <button type="button" className="btn btn--ghost" onClick={onCompose}>
              📝 تنظیم گزارش
            </button>
            <button type="button" className="btn btn--ghost" disabled={pending} onClick={onScreenCheck}>
              📸 ارسال به کارشناس فنی
            </button>
            <button type="button" className="btn btn--primary" disabled={pending} onClick={onComplete}>
              ✔️ ارسال برای تأیید فنی
            </button>
          </>
        )}
        {needsRevision && (
          <>
            <button type="button" className="btn btn--ghost" onClick={onCompose}>
              📝 اصلاح گزارش
            </button>
            <button type="button" className="btn btn--ghost" disabled={pending} onClick={onScreenCheck}>
              📸 ارسال به کارشناس فنی
            </button>
            <button type="button" className="btn btn--primary" disabled={pending} onClick={onComplete}>
              🔁 ارسال دوباره پس از اصلاح
            </button>
          </>
        )}
        {['technical_review', 'report_completed', 'closed'].includes(item.status) && (
          <button type="button" className="btn btn--ghost" onClick={onCompose}>
            👁️ دیدن گزارش
          </button>
        )}
      </footer>
    </article>
  );
}

/** شمارنده‌ی زنده‌ی زمان سپری‌شده — مبنای محاسبه‌ی کارانه ساعت سرور است، نه این عدد. */
function LiveTimer({ since }) {
  const [seconds, setSeconds] = useState(() => elapsedSince(since));

  useEffect(() => {
    setSeconds(elapsedSince(since));
    const timer = setInterval(() => setSeconds(elapsedSince(since)), 1000);
    return () => clearInterval(timer);
  }, [since]);

  return (
    <span className="timer" title="زمان سپری‌شده از دریافت پرونده">
      ⏱ <time>{formatDuration(seconds)}</time>
    </span>
  );
}

function CompleteModal({ item, pending, onClose, onSubmit }) {
  const [form, setForm] = useState({
    word_uploaded: false,
    jpeg_uploaded: false,
    paper_report: false,
    word_cloud_url: '',
    jpeg_cloud_url: '',
  });

  useEffect(() => {
    setForm({
      word_uploaded: false,
      jpeg_uploaded: false,
      paper_report: false,
      word_cloud_url: '',
      jpeg_cloud_url: '',
    });
  }, [item?.id]);

  if (!item) return null;

  const toggle = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.checked }));
  const setUrl = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const valid = form.word_uploaded || form.jpeg_uploaded;

  return (
    <Modal
      open
      title="ثبت تکمیل گزارش"
      onClose={onClose}
      width={520}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!valid || pending}
            onClick={() =>
              onSubmit({
                ...form,
                word_cloud_url: form.word_cloud_url.trim(),
                jpeg_cloud_url: form.jpeg_cloud_url.trim(),
              })
            }
          >
            {pending ? 'در حال ثبت…' : 'ثبت تکمیل'}
          </button>
        </>
      }
    >
      <p className="modal__lead">
        بیمار <strong><bdi>{item.name}</bdi></strong> — <bdi>{item.edit_type_name}</bdi>
      </p>

      <label className={`check ${form.word_uploaded ? 'is-checked' : ''}`}>
        <input type="checkbox" checked={form.word_uploaded} onChange={toggle('word_uploaded')} />
        <span>فایل Word گزارش را در فضای ابری آپلود کردم.</span>
      </label>
      {form.word_uploaded && (
        <div className="field field--nested">
          <input
            type="url"
            dir="ltr"
            placeholder="نشانی فایل Word (اختیاری)"
            value={form.word_cloud_url}
            onChange={setUrl('word_cloud_url')}
          />
        </div>
      )}

      <label className={`check ${form.jpeg_uploaded ? 'is-checked' : ''}`}>
        <input type="checkbox" checked={form.jpeg_uploaded} onChange={toggle('jpeg_uploaded')} />
        <span>تصویر JPEG گزارش را در فضای ابری آپلود کردم.</span>
      </label>
      {form.jpeg_uploaded && (
        <div className="field field--nested">
          <input
            type="url"
            dir="ltr"
            placeholder="نشانی تصویر JPEG (اختیاری)"
            value={form.jpeg_cloud_url}
            onChange={setUrl('jpeg_cloud_url')}
          />
        </div>
      )}

      <label className={`check ${form.paper_report ? 'is-checked' : ''}`}>
        <input type="checkbox" checked={form.paper_report} onChange={toggle('paper_report')} />
        <span>گزارش کاغذی تنظیم شد.</span>
      </label>

      {!valid && (
        <p className="alert alert--warning">
          دست‌کم یکی از فایل‌های Word یا JPEG باید آپلود شده باشد.
        </p>
      )}

      <p className="form__note">
        گزارش ابتدا نزد مسئول فنی می‌رود. کارانه پس از تأیید او نهایی و در
        کارنامه‌ی شما ثبت می‌شود.
      </p>
    </Modal>
  );
}

/** پیش‌بررسیِ دو عکسِ اجباریِ مانیتور (با دوربین گوشی) + یادداشتِ اختیاری. */
function ScreenCheckModal({ item, pending, onClose, onSubmit }) {
  const [photo1, setPhoto1] = useState(null);
  const [photo2, setPhoto2] = useState(null);
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(null);
  const camera1Ref = useRef(null);
  const camera2Ref = useRef(null);

  // پیش‌نویس: تصویرها و متن با بستن پنجره، عوض کردن تب یا رفرش از بین نمی‌روند و
  // فقط پس از ارسالِ موفق پاک می‌شوند (کدِ والد). ضبط صدا نگه داشته نمی‌شود.
  const [draftFor, setDraftFor] = useState(null);
  const itemId = item?.id;

  useEffect(() => {
    setPhoto1(null);
    setPhoto2(null);
    setText('');
    setRecording(null);
    setDraftFor(null);
    if (itemId == null) return undefined;

    let cancelled = false;
    loadDraft(itemId).then((draft) => {
      if (cancelled) return;
      if (draft) {
        setPhoto1(draft.photo1 || null);
        setPhoto2(draft.photo2 || null);
        setText(draft.text || '');
      }
      setDraftFor(itemId);
    });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  // ذخیره فقط بعد از بارگذاری پیش‌نویس، وگرنه حالتِ خالیِ اول روی آن می‌نشست.
  useEffect(() => {
    if (!item || draftFor !== item.id) return;
    saveDraft(item.id, { photo1, photo2, text });
  }, [item, draftFor, photo1, photo2, text]);

  // Ctrl+V: پرینت‌اسکرین از برنامه‌ی ادیت مستقیم از کلیپ‌بورد می‌آید. اولین
  // تصویر جای خالیِ اول را پر می‌کند، بعدی دوم را؛ از آن به بعد دومی جایگزین
  // می‌شود (آخرین اسکرین‌شات معمولاً همان است که ادیتور می‌خواهد).
  const photosRef = useRef({ photo1: null, photo2: null });
  photosRef.current = { photo1, photo2 };
  const pastedCount = useRef(0);

  useEffect(() => {
    if (!item) return undefined;
    const onPaste = (event) => {
      const images = Array.from(event.clipboardData?.items || [])
        .filter((entry) => entry.kind === 'file' && entry.type.startsWith('image/'))
        .map((entry) => entry.getAsFile())
        .filter(Boolean);
      if (!images.length) return; // متن چسبانده شد (مثلاً داخل یادداشت) — دست نزن.

      event.preventDefault();
      images.forEach((blob) => {
        pastedCount.current += 1;
        const extension = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const file = new File([blob], `screenshot-${pastedCount.current}.${extension}`, {
          type: blob.type,
        });
        if (!photosRef.current.photo1) {
          photosRef.current.photo1 = file;
          setPhoto1(file);
        } else {
          photosRef.current.photo2 = file;
          setPhoto2(file);
        }
      });
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [item]);

  if (!item) return null;

  // event.target.files یک FileList زنده است — اول باید خواندش، بعد پاک کرد.
  const pick = (setPhoto) => (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (file) setPhoto(file);
  };

  const valid = Boolean(photo1);
  const describe = (file) => (file ? `✓ ${(file.size / 1024).toFixed(0)} KB` : '');

  return (
    <Modal
      open
      title="ارسال به کارشناس فنی"
      onClose={onClose}
      width={520}
      footer={
        <>
          {(photo1 || photo2 || text) && (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={pending}
              onClick={() => {
                setPhoto1(null);
                setPhoto2(null);
                setText('');
              }}
            >
              پاک کردن پیش‌نویس
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            بستن (پیش‌نویس می‌ماند)
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!valid || pending}
            onClick={() =>
              onSubmit({
                photo1,
                photo2,
                text: text.trim(),
                audio: recording?.blob ?? null,
                audioSeconds: recording?.seconds ?? null,
              })
            }
          >
            {pending ? 'در حال ارسال…' : 'ارسال'}
          </button>
        </>
      }
    >
      <p className="modal__lead">
        بیمار <strong><bdi>{item.name}</bdi></strong> — <bdi>{item.edit_type_name}</bdi>
      </p>
      <p className="form__note">
        یک یا دو تصویر از صفحه‌ی برنامه‌ی ادیت بفرستید تا کارشناس فنی و بقیه‌ی تیم
        در گروه ببینند و یاد بگیرند. روی کامپیوتر: پرینت‌اسکرین بگیرید و همین‌جا
        <strong dir="ltr"> Ctrl+V </strong> بزنید. با گوشی: دکمه‌ی دوربین. تصویر با
        کیفیت اصلی و به‌صورت فایل ارسال می‌شود. این یک پیش‌بررسیِ آموزشی است و
        درخواستِ رسمیِ تأیید فنی را جایگزین نمی‌کند.
      </p>

      <input ref={camera1Ref} type="file" accept="image/*" capture="environment" onChange={pick(setPhoto1)} hidden />
      <input ref={camera2Ref} type="file" accept="image/*" capture="environment" onChange={pick(setPhoto2)} hidden />

      <div className="toolbar">
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => camera1Ref.current?.click()}>
          📷 تصویر ۱ {describe(photo1)}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => camera2Ref.current?.click()}>
          📷 تصویر ۲ (اختیاری) {describe(photo2)}
        </button>
      </div>

      <div className="field">
        <label htmlFor="screen-check-text">یادداشت متنی (اختیاری)</label>
        <textarea
          id="screen-check-text"
          rows={3}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="توضیحی برای کارشناس فنی…"
        />
      </div>

      <VoiceRecorder onChange={setRecording} disabled={pending} />

      {!valid && (
        <p className="alert alert--warning">دست‌کم یک تصویر لازم است.</p>
      )}
    </Modal>
  );
}
